import { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';

import { getLocation } from 'store/__router/selectors';

import { useHistory } from 'core/universalRouter/hooks/useHistory';

type UseQueryStateOptions<T> = {
  defaultValue?: T;
  parser?: (value: string) => T;
};

/**
 * Custom hooks to manage search params as state, currently it can handle
 * string and number only.
 * @param {string} paramKey - search param query
 */
const useQueryState = <T extends string | number>(
  paramKey: string,
  options: UseQueryStateOptions<T> = {},
): [T, (value: T) => void] => {
  const location = useSelector(getLocation);
  const history = useHistory();

  const searchParams = new URLSearchParams(location.search);
  const [queryValue, setQueryValue] = useState<T>(() => {
    const paramValue = searchParams.get(paramKey) as T;
    if (paramValue) {
      return options.parser ? options.parser(paramValue as string) : paramValue;
    }
    return options.defaultValue || paramValue;
  });

  const onChange = (value: T) => {
    const newSearchParams = new URLSearchParams(window.location.search);
    setQueryValue(value);
    newSearchParams.set(paramKey, typeof value !== 'string' ? value.toString() : value);
    history?.replace({
      pathname: location.pathname,
      search: newSearchParams.toString(),
      state: {
        // to prevent re-rendering the whole page
        shouldUpdate: false,
      },
    });
  };

  return [queryValue, onChange];
};

export const parseAsInt = (value: string) => parseInt(value);

export default useQueryState;
